import { useCallback, useEffect, useState } from "react";
import { Inbox, Bell, MessageSquareQuote, LogOut, Clock } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DataTable } from "@/components/admin/DataTable";
import { ReviewActions, STATUS_STYLES } from "@/components/admin/ReviewActions";
import { InquiryStatus } from "@/components/admin/InquiryStatus";
import { Stars } from "@/components/Stars";
import { adminApi } from "@/lib/adminApi";

const INQUIRY_COLUMNS = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email", render: (r) => <a href={`mailto:${r.email}`} className="text-royal hover:underline">{r.email}</a> },
  { key: "company", label: "Company" },
  { key: "service", label: "Service" },
  { key: "message", label: "Message", render: (r) => <span className="line-clamp-3 whitespace-pre-line text-xs">{r.message}</span> },
];

const WAITLIST_COLUMNS = [
  { key: "email", label: "Email", render: (r) => <a href={`mailto:${r.email}`} className="text-royal hover:underline">{r.email}</a> },
  { key: "product_name", label: "Product" },
];

const REVIEW_COLUMNS = [
  { key: "status", label: "Status", render: (r) => <span data-testid={`admin-review-status-${r.id}`} className={`rounded px-2 py-0.5 font-mono-sub text-[11px] font-semibold ${STATUS_STYLES[r.status]}`}>{r.status}</span> },
  { key: "rating", label: "Rating", render: (r) => <Stars rating={r.rating} /> },
  { key: "name", label: "Reviewer", render: (r) => <div><p className="font-semibold text-steel">{r.name}</p><p className="font-mono-sub text-[11px] text-mute">{[r.role, r.company].filter(Boolean).join(" · ") || r.email}</p></div> },
  { key: "text", label: "Review", render: (r) => <span className="line-clamp-4 text-xs">{r.text}</span> },
];

function StatCard({ Icon, label, value, hint, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-line bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-royal-light text-royal">
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>
        {hint && <span className="font-mono-sub text-[11px] text-mute">{hint}</span>}
      </div>
      <p className="mt-4 font-poster text-3xl font-extrabold text-steel leading-none">{value ?? "—"}</p>
      <p className="mt-1 font-mono-sub text-[11px] uppercase tracking-wider text-mute">{label}</p>
    </div>
  );
}

export function AdminDashboard({ admin, onLogout }) {
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({ inquiries: [], waitlist: [], reviews: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [s, inq, wl, rv] = await Promise.all([
      adminApi.get("/admin/stats"),
      adminApi.get("/admin/inquiries"),
      adminApi.get("/admin/waitlist"),
      adminApi.get("/admin/reviews"),
    ]);
    setStats(s.data);
    setData({ inquiries: inq.data.items, waitlist: wl.data.items, reviews: rv.data.items });
    setLoading(false);
  }, []);

  useEffect(() => {
    load().catch(() => setLoading(false));
  }, [load]);

  const handleLogout = async () => {
    await adminApi.post("/auth/logout").catch(() => {});
    onLogout();
  };

  return (
    <section className="py-12 max-w-6xl mx-auto px-6 space-y-8" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <span className="font-mono-sub text-xs text-royal uppercase tracking-wider font-bold">Private dashboard</span>
          <h1 className="font-poster text-3xl font-extrabold text-steel mt-1" data-testid="admin-dashboard-title">Signups & reviews</h1>
          <p className="font-mono-sub text-[11px] text-mute mt-1">Signed in as {admin.email}</p>
        </div>
        <button
          type="button"
          data-testid="admin-logout-button"
          onClick={handleLogout}
          className="inline-flex items-center gap-2 rounded border border-slate-300 px-4 py-2 font-mono-sub text-xs font-bold uppercase tracking-wider text-steel transition-colors hover:bg-white"
        >
          <LogOut className="h-3.5 w-3.5" aria-hidden="true" /> Sign out
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard Icon={Inbox} label="Consultation inquiries" value={stats?.inquiries} hint={stats ? `${stats.inquiries_new} new · ${stats.inquiries_won} won` : undefined} testId="admin-stat-inquiries" />
        <StatCard Icon={Bell} label="Waitlist signups" value={stats?.waitlist} testId="admin-stat-waitlist" />
        <StatCard Icon={MessageSquareQuote} label="Published reviews" value={stats?.reviews_approved} hint={`${stats?.reviews_total ?? 0} total`} testId="admin-stat-reviews" />
        <StatCard Icon={Clock} label="Reviews awaiting approval" value={stats?.reviews_pending} testId="admin-stat-pending" />
      </div>

      <Tabs defaultValue="inquiries" data-testid="admin-tabs">
        <TabsList className="bg-white border border-line">
          <TabsTrigger value="inquiries" data-testid="admin-tab-inquiries">Inquiries</TabsTrigger>
          <TabsTrigger value="waitlist" data-testid="admin-tab-waitlist">Waitlist</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="admin-tab-reviews">
            Reviews{stats?.reviews_pending ? ` (${stats.reviews_pending} pending)` : ""}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="inquiries" className="mt-4">
          <DataTable
            dataset="inquiries"
            title="Consultation inquiries"
            columns={INQUIRY_COLUMNS}
            rows={data.inquiries}
            loading={loading}
            actions={(row) => <InquiryStatus inquiry={row} onChanged={load} />}
            actionsLabel="Status"
          />
        </TabsContent>
        <TabsContent value="waitlist" className="mt-4">
          <DataTable dataset="waitlist" title="Product waitlist" columns={WAITLIST_COLUMNS} rows={data.waitlist} loading={loading} />
        </TabsContent>
        <TabsContent value="reviews" className="mt-4">
          <DataTable
            dataset="reviews"
            title="Customer reviews"
            columns={REVIEW_COLUMNS}
            rows={data.reviews}
            loading={loading}
            actions={(row) => <ReviewActions review={row} onChanged={load} />}
          />
        </TabsContent>
      </Tabs>
    </section>
  );
}
